import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { AlertTriangle, CheckCircle2, Eraser, FilePlus, X } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { useUsers, type AdminUser } from '@/features/admin/api'
import { dateTime, num, relativeTime } from '@/lib/format'

import {
  useCancelErasure,
  useErasureRequests,
  useExecuteErasure,
  useFileErasureRequest,
  type ErasureRow,
} from './api'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * DPDP right-to-erasure admin desk. Routes mounted under /admin/erasure.
 *
 * <p>The flow:
 *   <ul>
 *     <li>Manager files a request for a customer (UUID + reason).</li>
 *     <li>Request sits in REQUESTED until executed.</li>
 *     <li>Execute hard-deletes PII; audit trail keeps a pseudonymised reference.</li>
 *     <li>Cancel (with reason) is the side-door while still REQUESTED.</li>
 *   </ul>
 * </p>
 */
export function ErasurePage() {
  const pending = useErasureRequests('REQUESTED', 100)
  const executed = useErasureRequests('EXECUTED', 100)
  const cancelled = useErasureRequests('CANCELLED', 100)

  const users = useUsers()
  const empById = useMemo(() => {
    const m = new Map<string, AdminUser>()
    for (const u of users.data ?? []) m.set(u.userId, u)
    return m
  }, [users.data])

  const [tab, setTab] = useState<'pending' | 'history'>('pending')
  const [showFileForm, setShowFileForm] = useState(false)
  const [executeTarget, setExecuteTarget] = useState<ErasureRow | null>(null)
  const [cancelTarget, setCancelTarget] = useState<ErasureRow | null>(null)

  const history = useMemo(() => {
    const merged: ErasureRow[] = [...(executed.data ?? []), ...(cancelled.data ?? [])]
    merged.sort((a, b) => {
      const ta = Date.parse(a.executedAt ?? a.cancelledAt ?? a.createdAt)
      const tb = Date.parse(b.executedAt ?? b.cancelledAt ?? b.createdAt)
      return tb - ta
    })
    return merged
  }, [executed.data, cancelled.data])

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
            Tenant administration
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
            <Eraser className="h-5 w-5" /> Right-to-erasure
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            DPDP §12 erasure requests. Executing a request hard-deletes the
            customer's PII; the audit chain retains a pseudonymised reference
            to prove regulatory compliance.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowFileForm((s) => !s)}>
          <FilePlus className="h-3.5 w-3.5" />
          {showFileForm ? 'Hide form' : 'File new request'}
        </Button>
      </div>

      {showFileForm && <FileRequestForm onClose={() => setShowFileForm(false)} />}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Erasure requests</CardTitle>
              <CardDescription>
                {pending.data ? `${num(pending.data.length)} pending · ${num(history.length)} in history` : '—'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          <div className="flex flex-wrap items-center gap-2 px-5 pb-3">
            <Chip
              active={tab === 'pending'}
              onClick={() => setTab('pending')}
              count={pending.data?.length ?? 0}
            >
              Pending
            </Chip>
            <Chip
              active={tab === 'history'}
              onClick={() => setTab('history')}
              count={history.length}
            >
              History
            </Chip>
          </div>

          {tab === 'pending' ? (
            <PendingTable
              rows={pending.data}
              loading={pending.isPending}
              error={pending.isError}
              empById={empById}
              onExecute={(row) => setExecuteTarget(row)}
              onCancel={(row) => setCancelTarget(row)}
            />
          ) : (
            <HistoryTable
              rows={history}
              loading={executed.isPending || cancelled.isPending}
              empById={empById}
            />
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            CUSTOMER_PII_ERASE · audit retained · per-tenant
          </span>
        </CardFooter>
      </Card>

      {executeTarget && (
        <ExecuteConfirmModal
          row={executeTarget}
          onClose={() => setExecuteTarget(null)}
        />
      )}
      {cancelTarget && (
        <CancelModal row={cancelTarget} onClose={() => setCancelTarget(null)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// File a new request — Customer 360 will replace this eventually; for now
// a simple admin-side form taking the customer UUID + a free-text reason.
// ---------------------------------------------------------------------------

function FileRequestForm({ onClose }: { onClose: () => void }) {
  const file = useFileErasureRequest()
  const [customerId, setCustomerId] = useState('')
  const [reason, setReason] = useState('')

  const validId = UUID_RE.test(customerId.trim())
  const canSubmit = validId && reason.trim().length >= 5

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    file.mutate(
      { customerId: customerId.trim(), reason: reason.trim() },
      {
        onSuccess: () => {
          setCustomerId('')
          setReason('')
        },
      },
    )
  }

  const errorMsg = (() => {
    if (!file.isError) return null
    if (isAxiosError(file.error)) {
      const status = file.error.response?.status
      const data = file.error.response?.data as { detail?: string; message?: string } | undefined
      if (status === 403) return 'CUSTOMER_PII_ERASE is required.'
      if (status === 404) return 'Customer not found in this tenant.'
      return data?.detail ?? data?.message ?? `Request failed (HTTP ${status ?? '?'}).`
    }
    return 'Request failed.'
  })()

  return (
    <Card>
      <CardHeader>
        <CardTitle>File erasure request</CardTitle>
        <CardDescription>
          Once Customer 360 ships, this form moves there. The request lands in
          Pending and can be executed or cancelled.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Customer ID (UUID)</Label>
            <Input
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="font-mono text-xs"
              required
            />
            {customerId.length > 0 && !validId && (
              <p className="mt-1 text-2xs uppercase tracking-wider text-warning">
                Not a valid UUID
              </p>
            )}
          </div>
          <div>
            <Label>Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer invoked §12 DPDP rights via email dated…"
              rows={3}
              required
            />
            <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
              Recorded verbatim in the audit chain.
            </p>
          </div>

          {errorMsg && (
            <Alert variant="destructive">
              <AlertTitle>Could not file</AlertTitle>
              <AlertDescription>{errorMsg}</AlertDescription>
            </Alert>
          )}
          {file.isSuccess && (
            <Alert variant="positive">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Filed</AlertTitle>
              <AlertDescription>
                Request <span className="font-mono">{file.data.requestId}</span> is now Pending.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit || file.isPending}>
              <FilePlus className="h-3.5 w-3.5" />
              {file.isPending ? 'Filing…' : 'File request'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Pending table — REQUESTED rows with execute / cancel.
// ---------------------------------------------------------------------------

function PendingTable({
  rows,
  loading,
  error,
  empById,
  onExecute,
  onCancel,
}: {
  rows: ErasureRow[] | undefined
  loading: boolean
  error: boolean
  empById: Map<string, AdminUser>
  onExecute: (row: ErasureRow) => void
  onCancel: (row: ErasureRow) => void
}) {
  if (loading) return <SkeletonRows rows={4} cols={5} />
  if (error) {
    return (
      <div className="px-5 pb-4 text-sm text-negative">
        Couldn't load requests — CUSTOMER_PII_ERASE is required.
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-sm font-medium text-navy-900">Nothing pending</p>
        <p className="mt-1 text-xs text-muted-foreground">
          When a customer invokes their DPDP right to erasure, the request lands here.
        </p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
          <tr>
            <th className="px-5 py-2 font-medium">Customer</th>
            <th className="px-3 py-2 font-medium">Requested by</th>
            <th className="px-3 py-2 font-medium">Reason</th>
            <th className="px-3 py-2 font-medium">Filed</th>
            <th className="px-5 py-2 text-right font-medium">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const requester = row.requestedByUserId ? empById.get(row.requestedByUserId) : undefined
            return (
              <tr
                key={row.requestId}
                className="border-b border-slate-200 last:border-0 transition-colors hover:bg-navy-50"
              >
                <td className="px-5 py-2 font-mono text-xs text-navy-900" title={row.customerId}>
                  {row.customerId.slice(0, 8)}…
                </td>
                <td className="px-3 py-2 font-mono text-xs text-navy-900">
                  {requester?.empId ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 max-w-[24rem] text-xs text-navy-900">
                  <span className="block truncate" title={row.requestedReason ?? undefined}>
                    {row.requestedReason ?? <span className="text-muted-foreground">No reason recorded</span>}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground" title={dateTime(row.createdAt)}>
                  {relativeTime(row.createdAt)}
                </td>
                <td className="px-5 py-2 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => onCancel(row)}>
                      Cancel
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => onExecute(row)}>
                      Execute
                    </Button>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// History table — EXECUTED + CANCELLED.
// ---------------------------------------------------------------------------

function HistoryTable({
  rows,
  loading,
  empById,
}: {
  rows: ErasureRow[]
  loading: boolean
  empById: Map<string, AdminUser>
}) {
  if (loading) return <SkeletonRows rows={4} cols={5} />
  if (rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center">
        <p className="text-sm font-medium text-navy-900">No history yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Executed and cancelled requests will appear here.
        </p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
          <tr>
            <th className="px-5 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Customer</th>
            <th className="px-3 py-2 font-medium">By</th>
            <th className="px-3 py-2 font-medium">When</th>
            <th className="px-5 py-2 font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const executed = row.status === 'EXECUTED'
            const who = executed
              ? row.approvedByUserId
              : row.requestedByUserId // cancelled-by isn't surfaced separately on the row today
            const actor = who ? empById.get(who) : undefined
            const when = executed ? row.executedAt : row.cancelledAt
            const note = executed ? row.requestedReason : row.cancelledReason
            return (
              <tr
                key={row.requestId}
                className="border-b border-slate-200 last:border-0"
              >
                <td className="px-5 py-2">
                  <Badge tone={executed ? 'navy' : 'slate'}>{row.status}</Badge>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-navy-900" title={row.customerId}>
                  {row.customerId.slice(0, 8)}…
                </td>
                <td className="px-3 py-2 font-mono text-xs text-navy-900">
                  {actor?.empId ?? <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground" title={dateTime(when)}>
                  {relativeTime(when)}
                </td>
                <td className="px-5 py-2 max-w-[24rem] text-xs text-navy-900">
                  <span className="block truncate" title={note ?? undefined}>
                    {note ?? <span className="text-muted-foreground">—</span>}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Execute confirm modal — strongly-worded, two-step.
// ---------------------------------------------------------------------------

function ExecuteConfirmModal({ row, onClose }: { row: ErasureRow; onClose: () => void }) {
  const execute = useExecuteErasure()
  const [acknowledged, setAcknowledged] = useState(false)

  const errorMsg = (() => {
    if (!execute.isError) return null
    if (isAxiosError(execute.error)) {
      const status = execute.error.response?.status
      const data = execute.error.response?.data as { detail?: string; message?: string } | undefined
      if (status === 403) return 'CUSTOMER_PII_ERASE is required.'
      return data?.detail ?? data?.message ?? `Execute failed (HTTP ${status ?? '?'}).`
    }
    return 'Execute failed.'
  })()

  function onExecute() {
    if (!acknowledged || execute.isPending) return
    execute.mutate(row.requestId, {
      onSuccess: () => {
        setTimeout(onClose, 1500)
      },
    })
  }

  return (
    <ModalShell title="Execute erasure" onClose={onClose}>
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>This action is irreversible</AlertTitle>
        <AlertDescription>
          PII for the customer will be <span className="font-semibold">hard-deleted</span>.
          The audit trail is retained with a pseudonymised reference for
          compliance.
        </AlertDescription>
      </Alert>

      <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
        <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Customer</dt>
        <dd className="break-all font-mono text-xs text-navy-900">{row.customerId}</dd>
        <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Request</dt>
        <dd className="break-all font-mono text-xs text-navy-900">{row.requestId}</dd>
        <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Filed</dt>
        <dd className="text-navy-900">{dateTime(row.createdAt)}</dd>
        <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Reason</dt>
        <dd className="text-navy-900">{row.requestedReason ?? '—'}</dd>
      </dl>

      {/* C14: spell out exactly what will and won't be touched so the
          operator can make an informed decision. Without this, the
          modal reads as a generic "are you sure" and the operator has
          to remember DPDP semantics from a spec doc. */}
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-md border border-negative/35 bg-negative-soft/50 p-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-negative">
            Hard-deleted now
          </p>
          <ul className="mt-1.5 space-y-0.5 text-xs text-navy-900">
            <li>· Full name (replaced with "[erased]")</li>
            <li>· Primary &amp; secondary mobile numbers</li>
            <li>· Email address</li>
            <li>· Current &amp; permanent addresses</li>
            <li>· References / guarantor contacts</li>
            <li>· DNC flag &amp; reason</li>
          </ul>
        </div>
        <div className="rounded-md border border-positive/35 bg-positive-soft/40 p-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-positive">
            Preserved pseudonymously
          </p>
          <ul className="mt-1.5 space-y-0.5 text-xs text-navy-900">
            <li>· customer_id (the row stays)</li>
            <li>· All loans &amp; cases (linked by id)</li>
            <li>· Allocation history</li>
            <li>· Disposition + PTP events</li>
            <li>· Hash-chained audit trail</li>
            <li>· RBI 8-year regulatory floor honoured</li>
          </ul>
        </div>
      </div>

      <label className="mt-4 flex items-start gap-2 text-xs text-navy-900">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-navy-700 focus:ring-navy-500"
        />
        <span>
          I confirm that this customer has invoked their DPDP right to erasure
          and that hard-deletion is appropriate.
        </span>
      </label>

      {errorMsg && (
        <Alert variant="destructive" className="mt-3">
          <AlertTitle>Could not execute</AlertTitle>
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}
      {execute.isSuccess && (
        <Alert variant="positive" className="mt-3">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Executed</AlertTitle>
          <AlertDescription>
            {num(execute.data.rowsTouched)} row(s) of PII hard-deleted. Audit
            chain updated.
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={onExecute}
          disabled={!acknowledged || execute.isPending || execute.isSuccess}
        >
          {execute.isPending ? 'Executing…' : 'Execute erasure'}
        </Button>
      </div>
    </ModalShell>
  )
}

// ---------------------------------------------------------------------------
// Cancel modal — asks for a reason.
// ---------------------------------------------------------------------------

function CancelModal({ row, onClose }: { row: ErasureRow; onClose: () => void }) {
  const cancel = useCancelErasure()
  const [reason, setReason] = useState('')
  const canSubmit = reason.trim().length >= 5

  function onCancel() {
    if (!canSubmit || cancel.isPending) return
    cancel.mutate(
      { requestId: row.requestId, reason: reason.trim() },
      {
        onSuccess: () => {
          setTimeout(onClose, 1000)
        },
      },
    )
  }

  const errorMsg = (() => {
    if (!cancel.isError) return null
    if (isAxiosError(cancel.error)) {
      const status = cancel.error.response?.status
      const data = cancel.error.response?.data as { detail?: string; message?: string } | undefined
      if (status === 403) return 'CUSTOMER_PII_ERASE is required.'
      return data?.detail ?? data?.message ?? `Cancel failed (HTTP ${status ?? '?'}).`
    }
    return 'Cancel failed.'
  })()

  return (
    <ModalShell title="Cancel erasure request" onClose={onClose}>
      <p className="text-sm text-navy-900">
        Cancelling closes the request without deleting any PII. Provide a short
        reason for the audit chain.
      </p>
      <dl className="mt-3 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
        <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Customer</dt>
        <dd className="break-all font-mono text-xs text-navy-900">{row.customerId}</dd>
        <dt className="text-2xs uppercase tracking-wider text-muted-foreground">Request</dt>
        <dd className="break-all font-mono text-xs text-navy-900">{row.requestId}</dd>
      </dl>

      <div className="mt-4">
        <Label>Reason for cancelling</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Duplicate request, customer withdrew, etc."
          rows={3}
        />
      </div>

      {errorMsg && (
        <Alert variant="destructive" className="mt-3">
          <AlertTitle>Could not cancel</AlertTitle>
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}
      {cancel.isSuccess && (
        <Alert variant="positive" className="mt-3">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Cancelled</AlertTitle>
          <AlertDescription>Request closed. No PII was touched.</AlertDescription>
        </Alert>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
        <Button
          size="sm"
          onClick={onCancel}
          disabled={!canSubmit || cancel.isPending || cancel.isSuccess}
        >
          {cancel.isPending ? 'Cancelling…' : 'Cancel request'}
        </Button>
      </div>
    </ModalShell>
  )
}

// ---------------------------------------------------------------------------
// Modal shell — small centered dialog. Backdrop + esc close.
// ---------------------------------------------------------------------------

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm"
      />
      <div className="relative z-10 w-full max-w-lg rounded-md border border-slate-200 bg-white shadow-lift">
        <div className="flex h-12 items-center justify-between border-b border-slate-200 px-5">
          <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
            {title}
          </span>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}
